import React, { useEffect, useState } from 'react';
import { BrandType, CreateMealXlsType, XlsAddonsType, XlsMealAddonsType } from 'utils/types';
import PageHeaderText from 'components/base/page-header-text';
import MealXlsReviewItem from 'components/meal-xls-review-component';
import useNewMealXlsContext from 'contexts/new-meal-xls.context';
import {
  AddonCatFormFieldType,
  AddonFormFieldType,
  MealFormFieldType,
  MealFormType
} from 'components/meal-xls-review-component/types';
import { useNavigate } from 'react-router-dom';
import { ROUTE_KEYS } from 'utils/constants';
import useDashboardContext from 'contexts/dashboard.context';
import { apiService } from 'services/base.service';
import { handleApiError } from 'utils/functions';

const ReviewXlsMeals = () => {
  const { activeCokitchen, category } = useDashboardContext();
  const navigate = useNavigate();
  const [brandsList, setBrandsList] = useState<BrandType[]>([]);
  const { mappedMeals, reviewedMeals, setReviewedMeals, setMappedMeals } = useNewMealXlsContext();
  const { routePrefix } = useDashboardContext();
  const [openMeals, setOpenMeals] = useState<(string | undefined)[]>([]);
  const { baseMeals } = useNewMealXlsContext();
  const toggleAccordion = (idx: string) => {
    const mealExists = openMeals.find((i) => i === idx);
    if (mealExists) {
      setOpenMeals(openMeals?.filter((i) => i !== idx));
    } else {
      setOpenMeals([...openMeals, idx]);
    }
  };

  const handleReviewClick = (meal: Partial<CreateMealXlsType>) => {
    if (!meal.name || meal?.hasError) return;
    setReviewedMeals([...reviewedMeals, meal.name]);
  };

  const updateMealData = (
    meal: any,
    type: MealFormType,
    e: any,
    fieldType: MealFormFieldType,
    category?: string,
    imgUrl?: string[]
  ) => {
    if (type === 'dropdown') {
      if (meal?.meal_category && category) meal.meal_category.name = category;
    } else if (type === 'boolean' && fieldType === 'meal_category_horizontal') {
      meal.meal_category_horizontal = e?.target?.checked;
    } else if (type === 'text') {
      const value: string = e?.target?.value ?? e;
      meal[fieldType] = value;
    } else if (imgUrl && type === 'image') {
      meal.images = imgUrl;
    }
  };

  const updateMeal = (
    i: number,
    type: MealFormType,
    e: any,
    fieldType: MealFormFieldType,
    category?: string,
    imgUrl?: string[]
  ) => {
    const meal = mappedMeals[i];
    if (!meal) return;
    updateMealData(meal, type, e, fieldType, category, imgUrl);
    toggleMealError(meal);
    setMappedMeals([...mappedMeals]);
  };

  const updateAddonCategory = (
    mealIdx: number,
    addonIdx: number,
    e: any,
    fieldType: AddonCatFormFieldType,
    addonCat: XlsAddonsType[]
  ) => {
    const meal = mappedMeals[mealIdx];
    if (!meal) return;
    if (fieldType === 'meal_category_selection_type') {
      meal.addons[addonIdx][fieldType].name = e;
    } else if (fieldType === 'meal_default_name') {
      meal.addons[addonIdx][fieldType] = e;
    } else {
      const value = e?.target?.value ?? e;
      meal.addons[addonIdx][fieldType] = value;
    }

    toggleAddonCatError(addonCat);
    toggleMealError(meal);
    setMappedMeals([...mappedMeals]);
  };

  const updateAddon = (
    mealIdx: number,
    addonCatIdx: number,
    addonIdx: number,
    e: any,
    fieldType: AddonFormFieldType,
    addon: XlsMealAddonsType[]
  ) => {
    const meal = mappedMeals[mealIdx];
    if (!meal) return;
    const value = e?.target?.value ?? e;
    meal.addons[addonCatIdx].addons[addonIdx][fieldType] = value;
    toggleAddonError(addon);
    toggleMealError(meal);
    setMappedMeals([...mappedMeals]);
  };

  const addonCatHasError = (addonCat: XlsAddonsType[]) => {
    if (addonCat?.length === 0) return false;
    return addonCat.some((addon) => addon.hasError);
  };

  const addonHasError = (addons: XlsAddonsType[]) => {
    if (addons?.length === 0) return false;
    const hasError = addons?.some((addonCat) => addonCat.addons?.some((addon) => addon.hasError));
    return !!hasError;
  };

  const mealHasError = (meal: CreateMealXlsType) => {
    return (
      !meal?.name ||
      !meal?.base_meal_id ||
      !meal?.meal_category?.name ||
      addonCatHasError(meal?.addons) ||
      addonHasError(meal?.addons)
    );
  };

  const toggleMealError = (meal: CreateMealXlsType) => {
    if (mealHasError(meal)) {
      meal.hasError = true;
    } else {
      meal.hasError = false;
    }
  };

  const updateMealError = () => {
    const meals = mappedMeals.map((meal) => {
      if (!meal) return;
      const addon = toggleAddonCatError(meal?.addons);
      if (addon) toggleMealError(meal);
      return meal;
    });
    setMappedMeals([...meals]);
  };

  const toggleAddonCatError = (addoonCat: XlsAddonsType[]) => {
    const addon = addoonCat?.map((addon) => {
      if (
        !addon.name ||
        Number(addon.min_selection_no) >
          Number(addon.max_selection_no || !addon.meal_category_selection_type.name)
      ) {
        addon.hasError = false;
      } else {
        addon.hasError = false;
      }
      toggleAddonError(addon.addons);
    });
    return addon;
  };

  const toggleAddonError = (addons: XlsMealAddonsType[]) => {
    addons?.map((addon) => {
      if (!addon.name) {
        addon.hasError = true;
      } else {
        addon.hasError = false;
      }
    });
  };

  useEffect(() => {
    if (mappedMeals.length != 0) {
      setOpenMeals(mappedMeals?.map((_, i) => String(i)));
    }
  }, []);

  useEffect(() => {
    if (!mappedMeals || !mappedMeals?.length) {
      navigate(`${routePrefix}/${ROUTE_KEYS.NEW_MEAL_XLS}/${ROUTE_KEYS.NEW_MEAL_XLS_UPLOAD}`);
    }
    updateMealError();
  }, []);

  useEffect(() => {
    fetchBrands();
  }, [category]);

  const fetchBrands = async () => {
    if (!category) return;
    try {
      const params = {
        cokitchen_id: activeCokitchen.id
      };
      const res = await apiService.get('brand_management/brands', {
        params
      });
      const data: BrandType[] = res.data?.filter(
        (b: BrandType) => b.item_type === category.toUpperCase() && !b.archived
      );

      setBrandsList(data);
    } catch (e) {
      handleApiError(e);
    }
  };

  return (
    <div>
      <PageHeaderText
        title='Item details'
        description='Review and confirm uploaded items'
        tagText={`${mappedMeals?.length} ${mappedMeals?.length === 1 ? 'item' : 'items'} found`}
      />

      <div className='space-y-10'>
        {mappedMeals?.map((meal, i) => (
          <MealXlsReviewItem
            brandsList={brandsList}
            mealIndex={i}
            key={i}
            isReview={reviewedMeals.includes(meal?.name ?? '')}
            handleReviewClick={handleReviewClick}
            toggleAccordion={() => toggleAccordion(String(i))}
            show={openMeals.includes(String(i))}
            meal={meal as Partial<CreateMealXlsType>}
            updateMeal={updateMeal}
            updateAddonCategory={updateAddonCategory}
            updateAddon={updateAddon}
            baseMeals={baseMeals}
          />
        ))}
      </div>
    </div>
  );
};

export default ReviewXlsMeals;
